import {
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FocusOutlineDirective } from 'src/app/accessibility/directives/focus-outline/focus-outline.directive';
import { PresentationUtilsService } from 'src/app/presentations/services/presentation-utils.service';
import {
  CAP_ALERT_TEMPLATES,
  capAlertTemplateDescription,
} from '../../../services/cap-alerts/cap-alert-templates';
import {
  CapAlertCatalogTemplate,
  CapAlertsPickerChoice,
  CapAlertsPickerMode,
} from '../../../types/cap-alerts-page.types';

@Component({
  selector: 'cap-alert-template-picker',
  templateUrl: './cap-alert-template-picker.component.html',
  styleUrl: './cap-alert-template-picker.component.scss',
  imports: [MatButtonModule, MatIconModule, FocusOutlineDirective],
})
export class CapAlertTemplatePickerComponent {
  private readonly presentationUtilsService = inject(PresentationUtilsService);

  readonly templates = input<CapAlertCatalogTemplate[]>([]);
  readonly currentCode = input('');
  /** The id of the presentation the alert plays, when it plays one. */
  readonly currentPresentation = input('');
  readonly mode = input<CapAlertsPickerMode>('change');
  readonly showCustomPresentation = input(false);
  /** Only a Rise Administrator can choose a presentation. */
  readonly canChoosePresentation = input(false);

  readonly chosen = output<CapAlertsPickerChoice>();
  readonly back = output<void>();

  private readonly heading = viewChild<ElementRef<HTMLElement>>('heading');

  protected readonly customPresentation =
    CAP_ALERT_TEMPLATES.customPresentation;

  protected readonly isCustomPresentationCurrent = computed(
    () => !!this.currentPresentation() && !this.currentCode(),
  );

  /** Moves focus to the heading, so a screen reader announces the view that just replaced the previous one. */
  focusHeading(): void {
    this.heading()?.nativeElement.focus();
  }

  protected description(template: CapAlertCatalogTemplate): string {
    return capAlertTemplateDescription(template);
  }

  protected imageUrl(template: CapAlertCatalogTemplate): string | undefined {
    return this.presentationUtilsService.getProductImageUrl(
      template,
      'landscape',
    );
  }

  protected isCurrent(template: CapAlertCatalogTemplate): boolean {
    return template.productCode === this.currentCode();
  }

  protected chooseTemplate(template: CapAlertCatalogTemplate): void {
    this.chosen.emit({ kind: 'template', productCode: template.productCode });
  }

  protected choosePresentation(): void {
    if (this.canChoosePresentation()) {
      this.chosen.emit({ kind: 'presentation' });
    }
  }
}
